import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BackwardIcon,
  CircleInformation,
  ForwardIcon,
  LineChartIcon,
  PauseIcon,
  PlayIcon,
  RefreshIcon
} from "@/assets/icons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Timezone } from "@/constants/dateTime";
import { MESSAGE } from "@/constants/message";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";
import {
  setLegendFilters,
  setTimeSeriesGroupByFilter
} from "@/store/slice/mapSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetDataType,
  DatasetTimeseriesData,
  TimeseriesType
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { translateToUserTimezone } from "@/utils/date";
import { buildLegendFilterExpression } from "@/utils/map";
import {
  getTypeTimeSeries,
  handleFilterFeaturesGroupByAttribute,
  sortTimeSeriesData
} from "@/utils/map/timeseries";
import { compactNumber } from "@/utils/number";

import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { CategoricalChartState } from "recharts/types/chart/types";
import type {
  NameType,
  Payload,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { twMerge } from "tailwind-merge";

import { DatasetTypeIcon } from "../dataset/datasetList/DatasetTypeIcon";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious
} from "../ui/carousel";
import Divider from "../ui/Divider";

dayjs.extend(weekOfYear);

// Stable reference so the `?? {}` fallback doesn't return a new object on every
// useSelector run, which would otherwise trigger react-redux warnings and
// unnecessary re-renders.
const EMPTY_GROUP_BY_FILTER: Record<
  string,
  { time: string; attribute: string }
> = {};

interface TimeSeriesItemProps {
  readonly type: TimeseriesType;
  readonly data: DatasetTimeseriesData[];
  timezone: Timezone | undefined;
  currentIndex: number;
  onIndexChange: (index: number) => void;
}

interface TabTimeSeriesProps {
  key: string;
  label: string;
  value: string;
  spatialType: SpatialAggregationType;
  type: TimeseriesType;
  sortedData: DatasetTimeseriesData[];
  xAxisKey: string;
}

interface CustomTooltipTimeSeriesProps {
  active?: boolean;
  payload?: Payload<ValueType, NameType>[];
  activePoint?: DatasetTimeseriesData;
  compactNumber: (num: number, decimal?: number) => string;
}
const CustomTooltipTimeSeries = ({
  active,
  payload,
  activePoint,
  compactNumber
}: CustomTooltipTimeSeriesProps) => {
  if (!active || !payload?.length) return null;
  const value = payload.find(
    (p) => p.value !== null || p.value !== undefined
  )?.value;
  if (value === null || value === undefined) return null;
  return (
    <div className="rounded-2xl bg-[#2b2b2b] p-4 text-white shadow-lg max-w-80 flex flex-col justify-center items-center">
      {/* TITLE */}
      <p className="mb-1 text-[10px] font-semibold">
        {activePoint?.id ?? activePoint?.name ?? "Data Point"}
      </p>
      {/* ROW */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-2">
          {/* DOT */}
          <span className="h-3 w-3 rounded bg-emerald-500" />
          <span className="text-[10px] text-slate-200">
            {activePoint?.name || "Data Point"}
          </span>
        </div>
        {/* VALUE */}
        <span className="text-[10px] font-bold">
          {compactNumber(Number(value), 2)}
        </span>
      </div>
    </div>
  );
};

// onIndexChange owns the complete sync operation (map filter + legend color ranges).
// TimeSeriesItem only manages playback state and delegates index changes upward.
const TimeSeriesItem = ({
  type,
  data,
  timezone,
  currentIndex,
  onIndexChange
}: TimeSeriesItemProps) => {
  const [playing, setPlaying] = useState(false);

  const activePoint = data[currentIndex];

  const progressData = data.map((d, i) =>
    i <= currentIndex ? d : { ...d, value: null }
  );

  const setCurrentIndex = useCallback(
    (index: number) => {
      onIndexChange(index);
    },
    [onIndexChange]
  );

  const handleChartClick = (state: CategoricalChartState) => {
    if (state?.activeTooltipIndex == null) return;
    setCurrentIndex(state.activeTooltipIndex);
    setPlaying(false);
  };

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const labelActivePoint = (time: string): string => {
    if (type === "time") {
      return translateToUserTimezone(timezone, DatasetDataType.TYPE_TIME, time);
    } else if (type === "day") {
      return time.slice(0, 3); // Assuming time is in format "Monday", "Tuesday", etc.
    } else if (type === "week") {
      return `Week ${time}`;
    } else if (type === "date") {
      return dayjs(time).format("DD/MM/YYYY");
    } else {
      return time;
    }
  };

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setCurrentIndex(currentIndex >= data.length - 1 ? 0 : currentIndex + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [data, playing, currentIndex, setCurrentIndex]);

  return (
    <div className="w-full max-h-[264px]">
      <div className="flex justify-between">
        {/* RIGHT */}
        <div className="flex gap-3 min-h-[38px] mb-2">
          <div
            className={twMerge(
              "flex flex-col items-start justify-center w-24",
              type === "time" ? "" : "min-w-24"
            )}
          >
            <p className="text-sm font-bold">
              {labelActivePoint(activePoint?.time)}
            </p>
          </div>
          <Divider type="vertical" width="10" />
          <div className="flex gap-2 items-center">
            <p className="text-sm font-bold">
              {compactNumber(activePoint?.value, 2)}
            </p>
          </div>
        </div>
        {/* LEFT */}
        <div className="flex item-center gap-2">
          <button
            type="button"
            aria-label="backward button"
            disabled={currentIndex === 0 || playing}
            onClick={() => {
              setCurrentIndex(Math.max(currentIndex - 1, 0));
            }}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white-smoke text-gray-700 transition disabled:cursor-not-allowed disabled:bg-[#FBFBFC] group shadow-sm"
          >
            <BackwardIcon
              className="fill-txtBody group-disabled:fill-[#d5d5d5]"
              title="Backward icon"
            />
          </button>
          <button
            type="button"
            aria-label={playing ? "pause button" : "play button"}
            onClick={() => setPlaying((prev) => !prev)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white-smoke text-white transition"
          >
            {playing ? (
              <PauseIcon className="fill-txtBody" title="Pause icon" />
            ) : (
              <PlayIcon className="fill-txtBody" title="Play icon" />
            )}
          </button>
          <button
            type="button"
            aria-label="forward button"
            disabled={currentIndex >= data.length - 1 || playing}
            onClick={() => {
              setCurrentIndex(Math.min(currentIndex + 1, data.length - 1));
            }}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white-smoke text-gray-700 transition disabled:cursor-not-allowed disabled:bg-[#FBFBFC] group shadow-sm"
          >
            <ForwardIcon
              className="fill-txtBody group-disabled:fill-[#d5d5d5]"
              title="Forward icon"
            />
          </button>
          <button
            aria-label="refresh button"
            onClick={() => {
              setCurrentIndex(0);
              setPlaying(false);
            }}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white-smoke text-gray-700 transition"
          >
            <RefreshIcon
              className="fill-txtBody"
              width={24}
              height={24}
              title="Refresh icon"
            />
          </button>
        </div>
      </div>
      <ResponsiveContainer
        className="overflow-visible"
        width="100%"
        height={180}
      >
        <LineChart
          data={data}
          onClick={handleChartClick}
          onMouseMove={handleChartClick}
          margin={{ top: 10, right: 30, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray="4 4"
            vertical={false}
            stroke="#e5e7eb"
          />
          <XAxis
            dataKey="time"
            axisLine={false}
            padding={{ left: 5 }}
            minTickGap={20}
            tickFormatter={(v) => labelActivePoint(v)}
          />
          <YAxis
            domain={[min, max]}
            axisLine={false}
            tickLine={false}
            padding={{ bottom: 20 }}
            tickFormatter={(v) => compactNumber(Number(v))}
          />
          <Tooltip
            cursor={{ stroke: "#10b981", strokeDasharray: "4 4" }}
            content={
              <CustomTooltipTimeSeries
                activePoint={activePoint}
                compactNumber={compactNumber}
              />
            }
          />
          {/* BASE LINE */}
          <Line
            type="linear"
            dataKey="value"
            stroke="#cbd5e1"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {/* PROGRESS LINE (NO ANIMATION) */}
          <Line
            type="linear"
            data={progressData}
            dataKey="value"
            stroke="#10b981"
            strokeWidth={3}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* PLAYHEAD */}
          <ReferenceLine
            x={activePoint.time}
            stroke="#616161"
            strokeWidth={2}
            ifOverflow="extendDomain"
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-2 p-2 bg-white-smoke rounded-lg border border-whisper mb-2">
        <CircleInformation className="fill-txtBody" title="Information icon" />
        <p className="text-xs text-txtBody">
          {MESSAGE.TIMESERIES_PANEL_VIEW_DATA_DESCRIPTION}
        </p>
      </div>
    </div>
  );
};

// TimeSeriesModal syncs the map and legend with a selected time frame.
//
// Data flow:
//   tabsData (datasets with group_by set) →
//   onIndexChange(index) →
//     handleFilterFeaturesGroupByAttribute: MapLibre filter so only features
//       where properties[xAxisKey] === currentTime are rendered
//     handleUpdateColorRangesByTimeSeries: reads those filtered features from
//       the map source, recalculates color ranges, dispatches updateDatasetStyle
//       → Redux → LegendsInfo re-renders with time-frame-accurate legend
//
// Effect 1 evicts stale processedColorRangeRef entries when datasets are removed.
// Effect 2 handles initial sync on mount and when tabsData changes. processedColorRangeRef
export const TimeSeriesModal = ({
  selectedFeature,
  forceCollapse = false
}: {
  selectedFeature: SelectedFeature | null;
  forceCollapse?: boolean;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const effectiveCollapsed = forceCollapse || collapsed;
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const processedColorRangeRef = useRef<Record<string, string>>({});
  const dispatch = useDispatch();
  /**
   * Scrub position per time-series tab, keyed by `tab.key` (a hash of the
   * dataset's filters, h3Configuration, group_by and selected feature) rather
   * than the dataset id. Keying on the data-shape hash means any change that
   * alters the set of time points yields a fresh key that defaults to 0. The
   * one case the key can't catch — a raw-data refresh that shrinks the points
   * without changing the key — is handled by the out-of-bounds reset in the
   * cleanup effect below.
   */
  const [tabIndexes, setTabIndexes] = useState<Record<string, number>>({});
  const {
    timeSeriesGroupByFilter: timeSeriesGroupByFilterFromStore,
    legendFilters
  } = useSelector((state: RootState) => state.map);

  const timeSeriesGroupByFilter =
    timeSeriesGroupByFilterFromStore ?? EMPTY_GROUP_BY_FILTER;
  const { datasets, visualizationOrder, rawDatasets, filters } = useSelector(
    (state: RootState) => state.dataset
  );

  const { map, handleUpdateColorRangesByTimeSeries } = useMap();

  const { data: userProfile } = useGetUserProfileAPI();

  const timeSeriesDatasets = useMemo(
    () =>
      visualizationOrder
        .map((id) => datasets[id])
        .filter(
          (dataset) => dataset?.group_by && dataset.style.visibility !== "none"
        ),
    [visualizationOrder, datasets]
  );

  const tabsData: TabTimeSeriesProps[] = useMemo(() => {
    return timeSeriesDatasets
      .map((dataset) => {
        const datasetRaw = rawDatasets[dataset.data.id];
        if (!datasetRaw) return null;

        const { rows, columns } = datasetRaw;
        const h3Configuration = dataset.h3Configuration?.aggregationType;
        const categories = dataset.categories;

        const groupByCategories = categories.find((cat) => cat.group_by_attr);
        const xAxis = groupByCategories;

        const xAxisKey = xAxis?.attribute_name;
        const xAxisType = xAxis?.data_type;

        const type = getTypeTimeSeries(categories);

        const yAxis = categories.find(
          (cat) =>
            cat.data_type === DataSchemaType.INT &&
            cat.attribute_name.toLowerCase().includes("count") // heuristic to find the value column, can be improved with backend support for identifying value column
        )?.attribute_name;

        if (!xAxisKey || !yAxis) return null;

        const xIndex = columns.findIndex((col) => col.name === xAxisKey);
        const yIndex = columns.findIndex((col) => col.name === yAxis);
        const h3IdIndex = columns.findIndex((col) => col.name === "H3 ID"); // to find the index of H3 ID column for filtering features on map according to selected point in time series

        if (xIndex === -1 || yIndex === -1) return null;

        const grouped: Record<string, number> = {};
        const groupedTotalData: Record<string, number> = {};
        // if there is a selected feature from the same dataset, show the time series for that feature, otherwise show the aggregated time series for all features in the dataset
        const hasSelectedFeatureFromSameDataset =
          selectedFeature?.source === dataset.data.id;

        const filteredRow = hasSelectedFeatureFromSameDataset
          ? rows.filter((row) => {
              const featureH3Id = selectedFeature.properties?.["H3 ID"];
              const rowH3Id = row[h3IdIndex];
              return featureH3Id && rowH3Id && featureH3Id === rowH3Id;
            })
          : rows;

        for (const row of filteredRow) {
          const rawTime = row[xIndex];
          const rawValue = row[yIndex];

          const value = Number(rawValue);
          if (!rawTime || Number.isNaN(value) || rawTime === "<nil>") continue;

          const time =
            xAxisType === DataSchemaType.TEXT
              ? String(rawTime).trim()
              : String(rawTime);

          grouped[time] = (grouped[time] || 0) + value;
          groupedTotalData[time] = (groupedTotalData[time] || 0) + 1;
        }

        const data = Object.entries(grouped).map(([time, value]) => ({
          time,
          value:
            h3Configuration === "AVG" && groupedTotalData[time] > 0
              ? value / groupedTotalData[time]
              : value, // if aggregation type is average, divide the total value by the count to get the average
          name: yAxis,
          id: hasSelectedFeatureFromSameDataset
            ? selectedFeature?.properties?.["H3 ID"]
            : undefined // to keep track of the H3 ID for filtering features on map according to selected point in time series
        }));

        const sortedData = sortTimeSeriesData(
          data,
          type,
          userProfile?.timezone
        );

        return {
          // Changing key forces TimeSeriesItem to remount (resetting internal
          // play/pause state) and resets the tabIndexes scrub position to 0
          // whenever filters, h3Configuration, group_by, or the selected
          // feature change. Each of these alters the set of time points, so a
          // fresh key is the single signal that the data shape changed.
          key: JSON.stringify({
            filters: filters[dataset.data.id] ?? [],
            h3Config: dataset.h3Configuration,
            groupBy: dataset.group_by,
            selectedH3Id: hasSelectedFeatureFromSameDataset
              ? (selectedFeature?.properties?.["H3 ID"] ?? null)
              : null
          }),
          label: dataset.data.display_name || dataset.data.name,
          value: dataset.data.id,
          spatialType: dataset.data.spatial_aggregation_type,
          type,
          sortedData,
          xAxisKey
        };
      })
      .filter(
        (item): item is TabTimeSeriesProps =>
          item !== null && item.sortedData.length > 0 // only show in time series if there is data to show
      );
  }, [
    timeSeriesDatasets,
    rawDatasets,
    userProfile?.timezone,
    filters,
    selectedFeature
  ]);

  // Derived: the selected tab falls back to the first tab when the current
  // selection is no longer valid (e.g. the dataset was removed).
  const effectiveSelectedTab = useMemo(() => {
    if (tabsData.length === 0) return null;
    const isValid = tabsData.some((t) => t.value === selectedTab);
    return isValid ? selectedTab : tabsData[0].value;
  }, [tabsData, selectedTab]);

  // Cleanup when the set of tabs (or their data shape) changes:
  //   - evict stale processedColorRangeRef entries (keyed by tab.key) so a
  //     re-added dataset (or one whose data shape changed) always gets fresh
  //     color ranges on its next time-step.
  //   - prune tabIndexes entries (keyed by tab.key) whose key no longer exists,
  //     so the map doesn't grow unbounded as filters, group_by, aggregation, or
  //     the selected feature change over a session.
  //   - reset any surviving index that is now out of bounds back to 0. A
  //     filter/group_by/aggregation/feature change already yields a fresh key
  //     (→ defaults to 0), so this only fires for a raw-data refresh that
  //     shrinks the time points without changing tab.key.
  useEffect(() => {
    const activeTabKeys = new Set(tabsData.map((t) => t.key));
    for (const key of Object.keys(processedColorRangeRef.current)) {
      if (!activeTabKeys.has(key)) delete processedColorRangeRef.current[key];
    }

    const activeKeyLengths = new Map(
      tabsData.map((t) => [t.key, t.sortedData.length])
    );
    setTabIndexes((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      for (const [key, idx] of Object.entries(prev)) {
        const length = activeKeyLengths.get(key);
        if (length === undefined) {
          changed = true; // stale key → drop
          continue;
        }
        const safe = idx >= length ? 0 : idx; // out-of-bounds → reset to 0
        if (safe !== idx) changed = true;
        next[key] = safe;
      }
      return changed ? next : prev;
    });
  }, [tabsData]);

  useEffect(() => {
    if (tabsData.length === 0 || !map) return;
    // Mirror of timeSeriesGroupByFilter that we mutate locally and dispatch
    // once if anything actually changed, to avoid an extra Redux round-trip.
    const nextGroupByFilter: Record<
      string,
      { time: string; attribute: string }
    > = { ...timeSeriesGroupByFilter };
    let groupByFilterChanged = false;

    tabsData.forEach((tab) => {
      const currentIndex = tabIndexes[tab.key] ?? 0;
      const clampedIndex = Math.min(currentIndex, tab.sortedData.length - 1);
      const currentTime = tab.sortedData[clampedIndex]?.time;

      // Compose with the current legend filter so this initialization doesn't
      // wipe legend hide/show selections on mount or after tabsData changes.
      // NOTE: the legend filter is *preserved* here (re-init driven by data
      // changes), but *dropped* on a manual scrub — see onIndexChange below.
      const tabDataset = datasets[tab.value];
      const extraFilter = tabDataset
        ? buildLegendFilterExpression(tabDataset, legendFilters?.[tab.value])
        : null;
      handleFilterFeaturesGroupByAttribute(
        map,
        tab.xAxisKey,
        currentTime,
        tab.value,
        { extraFilter, dataset: tabDataset }
      );

      if (currentTime) {
        const prev = nextGroupByFilter[tab.value];
        if (
          !prev ||
          prev.time !== currentTime ||
          prev.attribute !== tab.xAxisKey
        ) {
          nextGroupByFilter[tab.value] = {
            time: currentTime,
            attribute: tab.xAxisKey
          };
          groupByFilterChanged = true;
        }
      }

      // Guard: skip color recalculation when this effect re-fires for the same
      // time value. Without this guard, a manual scrub via onIndexChange dispatches
      // updateDatasetStyle → datasets changes → tabsData gets a new reference →
      // this effect re-runs → second redundant recalculation fires.
      if (
        currentTime &&
        processedColorRangeRef.current[tab.key] !== currentTime
      ) {
        processedColorRangeRef.current[tab.key] = currentTime;
        handleUpdateColorRangesByTimeSeries(
          datasets[tab.value],
          tab.xAxisKey,
          currentTime
        );
      }
    });

    if (groupByFilterChanged) {
      dispatch(setTimeSeriesGroupByFilter(nextGroupByFilter));
    }
  }, [
    tabsData,
    map,
    timeSeriesGroupByFilter,
    tabIndexes,
    datasets,
    legendFilters,
    handleUpdateColorRangesByTimeSeries,
    dispatch
  ]);

  return (
    <>
      {tabsData && tabsData.length > 0 && (
        <>
          <button
            aria-label="toggle time series panel"
            aria-expanded={!effectiveCollapsed}
            aria-controls="timeseries-panel"
            className={twMerge(
              "absolute z-[99] right-24 bg-white px-4 py-2 rounded-lg text-teal-500 text-sm flex items-center justify-center gap-2 font-semibold transition-all duration-300",
              effectiveCollapsed ? "bottom-6 py-3" : "bottom-80 mb-2"
            )}
            onClick={() => setCollapsed((prev) => !prev)}
          >
            <LineChartIcon className="fill-teal-500" />
            Time Series
            {effectiveCollapsed ? (
              <ArrowUpIcon className=" fill-teal-500" title="Expand icon" />
            ) : (
              <ArrowDownIcon className=" fill-teal-500" title="Collapse icon" />
            )}
          </button>
          <div
            className={twMerge(
              "absolute right-24 transition-all duration-300 w-[calc(100vw-560px)]",
              effectiveCollapsed ? "-bottom-96" : "bottom-6"
            )}
          >
            <Tabs
              defaultValue={tabsData?.[0]?.value}
              value={effectiveSelectedTab || ""}
              className="w-full"
            >
              <TabsList className="w-[calc(100%-11rem)] px-0 pb-2 bg-transparent flex justify-start relative">
                <Carousel
                  opts={{
                    align: "start",
                    dragFree: true
                  }}
                  className="w-full"
                >
                  <CarouselContent classNameContainer="px-4">
                    {tabsData?.map((tab, index) => (
                      <TabsTrigger
                        key={tab?.value}
                        value={tab?.value}
                        className={twMerge(
                          "data-[state=active]:border-emerald-500 border-b border-neutral-200 text-sm text-gray-600 bg-white p-3 !rounded-none !shadow-none",
                          index === 0 ? "!rounded-tl-xl" : "",
                          index === tabsData?.length - 1 ? "!rounded-tr-xl" : ""
                        )}
                        onClick={() => {
                          setSelectedTab(tab.value);
                        }}
                      >
                        <DatasetTypeIcon
                          type={tab?.spatialType}
                          className="size-5 mr-1"
                        />
                        {tab?.label}
                      </TabsTrigger>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious
                    renderButton={({ scrollPrev, canScrollPrev }) => (
                      <button
                        className={twMerge(
                          "text-xl p-2 bg-white rounded-tl-xl absolute left-0 top-0 flex items-center justify-center",
                          !canScrollPrev && "hidden"
                        )}
                        onClick={scrollPrev}
                      >
                        <ArrowLeftIcon
                          width={28}
                          height={28}
                          className="fill-txtBody"
                        />
                      </button>
                    )}
                  />
                  <CarouselNext
                    renderButton={({ scrollNext, canScrollNext }) => (
                      <button
                        className={twMerge(
                          "text-xl p-2 bg-white rounded-tr-xl absolute right-0 top-0 flex items-center justify-center",
                          !canScrollNext && "hidden"
                        )}
                        onClick={scrollNext}
                      >
                        <ArrowRightIcon
                          width={28}
                          height={28}
                          className="fill-txtBody"
                        />
                      </button>
                    )}
                  />
                </Carousel>
              </TabsList>
              <div className="rounded-b-2xl rounded-tr-2xl bg-white shadow-xl px-4 py-2 overflow-visible">
                {tabsData?.map((tab) => {
                  return (
                    <TabsContent key={tab.value} value={tab.value}>
                      <TimeSeriesItem
                        key={tab.key}
                        type={tab.type}
                        data={tab.sortedData}
                        timezone={userProfile?.timezone}
                        currentIndex={Math.min(
                          tabIndexes[tab.key] ?? 0,
                          Math.max(0, tab.sortedData.length - 1)
                        )}
                        onIndexChange={(index) => {
                          const ds = datasets[tab.value];
                          const newTime = tab.sortedData[index]?.time;
                          if (!ds || !newTime) return;
                          // Changing the time-series index resets the layer to
                          // full data: drop any active legend hide/show filter
                          // for this dataset so only the new time filter applies.
                          // Counterpart to the init effect above, which instead
                          // *preserves* the legend filter on data-driven re-init.
                          if (legendFilters?.[tab.value]) {
                            const nextLegendFilters = { ...legendFilters };
                            delete nextLegendFilters[tab.value];
                            dispatch(setLegendFilters(nextLegendFilters));
                          }
                          handleFilterFeaturesGroupByAttribute(
                            map,
                            tab.xAxisKey,
                            newTime,
                            tab.value,
                            { dataset: ds }
                          );
                          handleUpdateColorRangesByTimeSeries(
                            ds,
                            tab.xAxisKey,
                            newTime
                          );
                          // Mark this time as processed so Effect 3 skips the
                          // redundant re-run triggered by the Redux dispatch above.
                          processedColorRangeRef.current[tab.key] = newTime;

                          // Local UI state for the slider position, keyed by
                          // tab.key so it resets when the data shape changes.
                          setTabIndexes((prev) => ({
                            ...prev,
                            [tab.key]: index
                          }));
                          // Publish the new time frame (filter) so
                          // useLegendFilter picks up the change.
                          dispatch(
                            setTimeSeriesGroupByFilter({
                              ...timeSeriesGroupByFilter,
                              [tab.value]: {
                                time: newTime,
                                attribute: tab.xAxisKey
                              }
                            })
                          );
                        }}
                      />
                    </TabsContent>
                  );
                })}
              </div>
            </Tabs>
          </div>
        </>
      )}
    </>
  );
};
